import type { PageViewport } from "pdfjs-dist";

export type PdfBox = [number, number, number, number];

const MIN_WIDTH_PT = 60;
const MIN_HEIGHT_PT = 25;

export interface BoxPickerOptions {
  canvas: HTMLCanvasElement;
  viewport: PageViewport;
  onChange: (box: PdfBox) => void;
}

/** Draws a draggable rectangle directly on top of the already-rendered PDF
 * page (pointer + touch capable, via Pointer Events -- touch-action is set
 * in CSS, not inline, to keep the strict CSP's style-src intact). A snapshot
 * of the rendered page is taken up front and restored before each redraw, so
 * we never need a second (CSP-unfriendly, position:absolute) overlay canvas. */
export function attachBoxPicker(options: BoxPickerOptions): () => void {
  const { canvas, viewport, onChange } = options;
  const ctx = canvas.getContext("2d")!;
  const pageSnapshot = ctx.getImageData(0, 0, canvas.width, canvas.height);

  let start: { x: number; y: number } | null = null;

  function canvasPoint(event: PointerEvent): { x: number; y: number } {
    const bounds = canvas.getBoundingClientRect();
    const scaleX = canvas.width / bounds.width;
    const scaleY = canvas.height / bounds.height;
    return {
      x: (event.clientX - bounds.left) * scaleX,
      y: (event.clientY - bounds.top) * scaleY,
    };
  }

  function draw(x1: number, y1: number, x2: number, y2: number): void {
    ctx.putImageData(pageSnapshot, 0, 0);
    ctx.strokeStyle = "#2563eb";
    ctx.lineWidth = 2;
    ctx.strokeRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
  }

  function toPdfBox(canvasX1: number, canvasY1: number, canvasX2: number, canvasY2: number): PdfBox {
    const [px1, py1] = viewport.convertToPdfPoint(canvasX1, canvasY1);
    const [px2, py2] = viewport.convertToPdfPoint(canvasX2, canvasY2);
    let x1 = Math.min(px1, px2);
    let x2 = Math.max(px1, px2);
    let y1 = Math.min(py1, py2);
    let y2 = Math.max(py1, py2);
    if (x2 - x1 < MIN_WIDTH_PT) x2 = x1 + MIN_WIDTH_PT;
    if (y2 - y1 < MIN_HEIGHT_PT) y2 = y1 + MIN_HEIGHT_PT;
    return [x1, y1, x2, y2];
  }

  function onPointerDown(event: PointerEvent): void {
    canvas.setPointerCapture(event.pointerId);
    start = canvasPoint(event);
    event.preventDefault();
  }

  function onPointerMove(event: PointerEvent): void {
    if (!start) return;
    const point = canvasPoint(event);
    draw(start.x, start.y, point.x, point.y);
    event.preventDefault();
  }

  function onPointerUp(event: PointerEvent): void {
    if (!start) return;
    const point = canvasPoint(event);
    onChange(toPdfBox(start.x, start.y, point.x, point.y));
    start = null;
    event.preventDefault();
  }

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);

  return () => {
    canvas.removeEventListener("pointerdown", onPointerDown);
    canvas.removeEventListener("pointermove", onPointerMove);
    canvas.removeEventListener("pointerup", onPointerUp);
  };
}

/** Draws a fixed, read-only box (stampit mode: the position comes from the
 * existing field's /Rect and must not be user-editable) directly onto the
 * already-rendered page canvas. */
export function drawReadOnlyBox(canvas: HTMLCanvasElement, viewport: PageViewport, box: PdfBox): void {
  const ctx = canvas.getContext("2d")!;
  const [x1, y1, x2, y2] = box;
  const [cx1, cy1] = viewport.convertToViewportPoint(x1, y1);
  const [cx2, cy2] = viewport.convertToViewportPoint(x2, y2);
  ctx.strokeStyle = "#9333ea";
  ctx.setLineDash([6, 4]);
  ctx.lineWidth = 2;
  ctx.strokeRect(Math.min(cx1, cx2), Math.min(cy1, cy2), Math.abs(cx2 - cx1), Math.abs(cy2 - cy1));
}
